import React, { useState, useEffect } from 'react';

const API_URL = 'https://backend.hira-tech.co.uk/order/company-invoice-details/';

const Details = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    website: '',
    email: '',
    bank_name: '',
    account_title: '',
    sort_code: '',
    account_number: '',
    iban: '',
    bic: '',
    branch: '',
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(API_URL);
      if (!response.ok) throw new Error('Fetch failed');
      const data = await response.json();
      setFormData(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load data');
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const response = await fetch(API_URL, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, id: 1 }), // Assuming id=1 for update
      });
      if (!response.ok) throw new Error('Update failed');
      alert('Changes saved successfully');
      fetchData(); // Refresh after save
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Failed to save changes');
    }
  };

  const containerStyle = {
    backgroundColor: '#f8fafc',
    padding: '30px 20px',
    minHeight: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  };

  const cardStyle = {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
    maxWidth: '900px',
    margin: '0 auto',
    overflow: 'hidden',
  };

  const headerStyle = {
    padding: '25px 30px',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
  };

  const titleStyle = {
    color: '#1e293b',
    fontSize: '24px',
    fontWeight: '600',
    marginBottom: '5px',
  };

  const subtitleStyle = {
    color: '#64748b',
    fontSize: '14px',
    fontWeight: '400',
  };

  const sectionStyle = {
    padding: '30px',
  };

  const sectionTitleStyle = {
    color: '#1e293b',
    fontSize: '18px',
    fontWeight: '600',
    marginBottom: '25px',
    display: 'flex',
    alignItems: 'center',
  };

  const sectionTitleIcon = {
    width: '4px',
    height: '18px',
    backgroundColor: '#3b82f6',
    borderRadius: '2px',
    marginRight: '10px',
  };

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '25px',
    flexWrap: 'wrap',
    gap: '20px',
  };

  const fieldGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    flex: '1',
    minWidth: '250px',
  };

  const labelStyle = {
    fontWeight: '500',
    color: '#475569',
    marginBottom: '8px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
  };

  const requiredMark = {
    color: '#ef4444',
    marginLeft: '4px',
  };

  const inputStyle = {
    padding: '12px 16px',
    borderRadius: '8px',
    border: '1px solid #cbd5e1',
    fontSize: '15px',
    backgroundColor: '#fff',
    transition: 'all 0.2s ease',
    outline: 'none',
  };

  const inputFocusStyle = {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
  };

  const sectionDivider = {
    height: '1px',
    backgroundColor: '#e2e8f0',
    margin: '30px 0',
  };

  const footerStyle = {
    padding: '25px 30px',
    borderTop: '1px solid #e2e8f0',
    backgroundColor: '#f8fafc',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '15px',
  };

  const buttonStyle = {
    padding: '12px 28px',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: 'none',
  };

  const cancelButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#f1f5f9',
    color: '#475569',
  };

  const saveButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#3b82f6',
    color: 'white',
  };

  // Handle input focus
  const handleInputFocus = (e) => {
    e.target.style.borderColor = inputFocusStyle.borderColor;
    e.target.style.boxShadow = inputFocusStyle.boxShadow;
  };

  // Handle input blur
  const handleInputBlur = (e) => {
    e.target.style.borderColor = '#cbd5e1';
    e.target.style.boxShadow = 'none';
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <div style={titleStyle}>Company Information</div>
          <div style={subtitleStyle}>Update your company and banking details</div>
        </div>

        <div style={sectionStyle}>
          {/* Company Details */}
          <div style={sectionTitleStyle}>
            <div style={sectionTitleIcon}></div>
            Company Details
          </div>
          
          <div style={rowStyle}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                Company Name
                <span style={requiredMark}>*</span>
              </label>
              <input 
                style={inputStyle} 
                type="text" 
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="Enter company name"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Phone</label>
              <input 
                style={inputStyle} 
                type="text" 
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+1 (555) 123-4567"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Address Line 1</label>
              <input 
                style={inputStyle} 
                type="text" 
                name="address_line1"
                value={formData.address_line1}
                onChange={handleChange}
                placeholder="123 Main Street"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Address Line 2</label>
              <input 
                style={inputStyle} 
                type="text" 
                name="address_line2"
                value={formData.address_line2}
                onChange={handleChange}
                placeholder="Suite 400"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Website</label>
              <input 
                style={inputStyle} 
                type="text" 
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="www.example.com"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>
                Email Address
                <span style={requiredMark}>*</span>
              </label>
              <input 
                style={inputStyle} 
                type="email" 
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="contact@company.com"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
          </div>

          <div style={sectionDivider}></div>

          {/* Bank Details */}
          <div style={sectionTitleStyle}>
            <div style={sectionTitleIcon}></div>
            Bank Details
          </div>
          
          <div style={rowStyle}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Bank Name</label>
              <input 
                style={inputStyle} 
                type="text" 
                name="bank_name"
                value={formData.bank_name}
                onChange={handleChange}
                placeholder="e.g. Chase Bank"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Account Title</label>
              <input 
                style={inputStyle} 
                type="text" 
                name="account_title"
                value={formData.account_title}
                onChange={handleChange}
                placeholder="Company Name LLC"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
          </div>

          <div style={rowStyle}>
            {/* <div style={fieldGroupStyle}>
              <label style={labelStyle}>Sort Code</label>
              <input 
                style={inputStyle} 
                type="text" 
                name="sort_code"
                value={formData.sort_code}
                onChange={handleChange}
                placeholder="12-34-56"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div> */}
            
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Account Number</label>
              <input 
                style={inputStyle} 
                type="text" 
                name="account_number"
                value={formData.account_number}
                onChange={handleChange}
                placeholder="123456789"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
          </div>

          <div style={rowStyle}>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>Branch</label>
              <input 
                style={inputStyle} 
                type="text" 
                name="branch"
                value={formData.branch}
                onChange={handleChange}
                placeholder=""
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
            <div style={fieldGroupStyle}>
              <label style={labelStyle}>IBAN</label>
              <input 
                style={inputStyle} 
                type="text" 
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                placeholder="GB29 NWBK 6016 1331 9268 19"
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
              />
            </div>
            
            
          </div>
        </div>

        <div style={footerStyle}>
          <button 
            style={cancelButtonStyle} 
            onClick={() => fetchData()} // Reset to original
            onMouseOver={e => e.target.style.backgroundColor = '#e2e8f0'} 
            onMouseOut={e => e.target.style.backgroundColor = '#f1f5f9'}
          >
            Cancel
          </button>
          <button 
            style={saveButtonStyle} 
            onClick={handleSave}
            onMouseOver={e => e.target.style.backgroundColor = '#2563eb'} 
            onMouseOut={e => e.target.style.backgroundColor = '#3b82f6'}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default Details;